import React from 'react';
import { Comment } from '../../entities/Comment';
import { Button } from '../ui/button.jsx';
import { MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { he, enUS } from 'date-fns/locale'; // הוספה: ייבוא חסר של enUS
import { useNavigate } from 'react-router-dom';
import { toast } from '../ui/use-toast';
import linkifyHtml from 'linkify-html';
import { useTranslation } from 'react-i18next';

// --- הוספה: פונקציית העזר החסרה להטמעת תמונות ---
function embedImagesInHtml(html) {
    if (!html) return '';
    html = html.replace(
        /(https?:\/\/[^\s<"]+\.(?:png|jpe?g|gif|webp|svg))/gi,
        (url) => `<div style="display:flex;justify-content:center;margin:1rem 0;"><img src="${url}" alt="Image from comment" style="max-width:100%;border-radius:8px;"/></div>`
    );
    html = linkifyHtml(html, { target: { url: '_blank' }, rel: 'noopener noreferrer' });
    return html;
}

// --- הוספה: פונקציית העזר החסרה לבדיקת תוקף טוקן ---
function isTokenExpired(token) {
    if (!token) return true;
    try {
        const [, payloadBase64] = token.split('.');
        const payload = JSON.parse(atob(payloadBase64));
        return payload.exp && Date.now() >= payload.exp * 1000;
    } catch (e) {
        return true;
    }
}

export default function CommentSection({ postId }) {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language.startsWith('he') ? he : enUS;

    const [comments, setComments] = React.useState([]);
    const [newComment, setNewComment] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitMessage, setSubmitMessage] = React.useState('');
    const [submitError, setSubmitError] = React.useState(false);
    const [isTokenValid, setIsTokenValid] = React.useState(true);

    const isLoggedIn = Boolean(localStorage.getItem('token'));
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const navigate = useNavigate();

    // --- הוספה: הפונקציה החסרה לטעינת תגובות ---
    const loadComments = React.useCallback(async () => {
        if (postId) {
            const fetchedComments = await Comment.listForPost(postId);
            setComments(fetchedComments);
        }
    }, [postId]);

    React.useEffect(() => {
        loadComments();
    }, [loadComments]);

    React.useEffect(() => {
        if (submitMessage) {
            const t = setTimeout(() => setSubmitMessage(''), 6000);
            return () => clearTimeout(t);
        }
    }, [submitMessage]);

    React.useEffect(() => {
        const token = localStorage.getItem('token');
        setIsTokenValid(!isTokenExpired(token));
    }, [isLoggedIn, newComment]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        const token = localStorage.getItem('token');

        if (isTokenExpired(token)) {
            localStorage.removeItem('token');
            localStorage.removeItem('currentUser');
            setSubmitMessage(t('comments.expired_message'));
            setSubmitError(true);
            toast({
                variant: "destructive",
                title: t('toast.session_expired_title'),
                description: t('toast.session_expired_desc')
            });
            setTimeout(() => navigate('/login'), 2000);
            return;
        }

        setIsSubmitting(true);
        try {
            await Comment.create(postId, newComment, token);
            setNewComment('');
            setSubmitMessage(t('comments.success_message'));
            setSubmitError(false);
            loadComments(); // רענון התגובות אחרי הוספת תגובה חדשה
        } catch (error) {
            setSubmitMessage(t('comments.error_message'));
            setSubmitError(true);
            console.error('Error posting comment:', error);
        }
        setIsSubmitting(false);
    };

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <MessageSquare className="w-6 h-6" />
                {t('comments.title')}
            </h2>
            {submitMessage && (
                <div className={`mb-4 text-center font-bold ${submitError ? 'text-red-700' : 'text-green-700'}`}>
                    {submitMessage}
                </div>
            )}
            {isLoggedIn && !isTokenValid && (
                <div className="mb-4 text-center text-red-600 font-bold">
                    {t('comments.expired_message')}
                </div>
            )}
            {isLoggedIn ? (
                <form onSubmit={handleSubmit} className="mb-8">
                    <div className="mb-2 text-gray-700 text-sm text-right">
                        {t('comments.will_be_published_as')} <b>{user.fullName || user.email || t('comments.anonymous')}</b>
                    </div>
                    <ReactQuill
                        theme="snow"
                        value={newComment}
                        onChange={setNewComment}
                        placeholder={t('forms.comment_placeholder')}
                        modules={{ toolbar: [['bold', 'italic', 'underline'], ['link']] }}
                        style={{ direction: 'rtl', minHeight: 100 }}
                    />
                    <Button
                        type="submit"
                        disabled={isSubmitting || !newComment.trim() || !isTokenValid}
                        className={`transition-colors mt-4 ${!isTokenValid ? "bg-gray-300 text-gray-400 cursor-not-allowed border-gray-200" : ""}`}
                    >
                        {isSubmitting
                            ? t('comments.submitting_button')
                            : !isTokenValid
                                ? t('comments.session_expired_button')
                                : t('comments.submit_button')}
                    </Button>
                </form>
            ) : (
                <div className="text-center text-gray-400 mb-8">
                    {t('comments.login_prompt')}
                </div>
            )}
            <div className="space-y-6">
                {comments.map((comment) => (
                    <div key={comment._id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-medium">
                                {comment.author?.fullName || t('comments.anonymous')}
                            </div>
                            <time className="text-sm text-gray-500">
                                {format(new Date(comment.createdAt), 'dd MMMM yyyy', { locale: dateLocale })}
                            </time>
                        </div>
                        <div
                            className="text-gray-700 prose"
                            dangerouslySetInnerHTML={{ __html: embedImagesInHtml(comment.content) }}
                        />
                    </div>
                ))}
                {comments.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                        {t('comments.no_comments')}
                    </p>
                )}
            </div>
        </div>
    );
}